import { useReducedMotion, type Transition } from 'motion/react';
import {
  fadeRiseVariants,
  isLowEndDevice,
  motionTokens,
  staggerContainer,
  staggerItem,
} from '@/utils/motionTokens';

export { motionTokens, staggerContainer, staggerItem, fadeRiseVariants };

/** Shared fade/translate presets — opacity + translateY only. */
export const fadeRiseTransition: Transition = {
  duration: motionTokens.duration.normal,
  ease: motionTokens.easing.smooth,
};

export const pageEnterTransition: Transition = {
  duration: motionTokens.duration.slow,
  ease: motionTokens.easing.smooth,
};

export function useMotionSafe() {
  const prefersReducedMotion = useReducedMotion();
  const lowEnd = isLowEndDevice();
  const reduce = Boolean(prefersReducedMotion) || lowEnd;
  const durationScale = lowEnd && !prefersReducedMotion ? 0.55 : 1;

  return {
    prefersReducedMotion: reduce,
    durationScale,
    fadeRise: reduce
      ? { initial: false as const, animate: { opacity: 1 }, transition: { duration: 0 } }
      : {
          initial: { opacity: 0, y: motionTokens.distance.md },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: motionTokens.duration.normal * durationScale,
            ease: motionTokens.easing.smooth,
          },
        },
    pageEnter: reduce
      ? { initial: false as const, animate: { opacity: 1 }, transition: { duration: 0 } }
      : {
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          transition: {
            duration: motionTokens.duration.slow * durationScale,
            ease: motionTokens.easing.smooth,
          },
        },
    staggerContainer: reduce
      ? { hidden: {}, visible: { transition: { staggerChildren: 0, delayChildren: 0 } } }
      : staggerContainer,
    staggerItem: reduce
      ? {
          hidden: { opacity: 1, y: 0 },
          visible: { opacity: 1, y: 0, transition: { duration: 0 } },
        }
      : {
          ...staggerItem,
          visible: {
            ...staggerItem.visible,
            transition: {
              ...staggerItem.visible.transition,
              duration: motionTokens.duration.normal * durationScale,
            },
          },
        },
    hoverLift: reduce
      ? {}
      : {
          whileHover: { y: -2 },
          transition: {
            duration: motionTokens.duration.fast,
            ease: motionTokens.easing.sharp,
          },
        },
  };
}
